import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import API from "../services/api";
import { Building2, ChevronRight, Gift, LogOut, Share2, User, Wallet } from "lucide-react";
import { shareAppLink } from "../utils/appInteractions";

function Profile() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [wallet, setWallet] = useState({ balance: 0 });

  useEffect(() => {
    if (!user?.id) { navigate("/login"); return; }
    API.get(`/wallet/${user.id}`)
      .then((res) => setWallet(res.data || { balance: 0 }))
      .catch(() => setWallet({ balance: 0 }));
  }, [navigate, user]);

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("posm_session_active");
    navigate("/login");
  };

  const shareApp = async () => {
    const referralCode = String(user?.referral_code || "").trim();
    const url = `${window.location.origin}/login?tab=register${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ""}`;
    const text = referralCode
      ? `Join Play Online Satta Matka with my referral code ${referralCode}`
      : "Join Play Online Satta Matka";
    try {
      const shared = await shareAppLink({ title: "Play Online Satta Matka", text, url });
      if (!shared) alert("Share link copied");
    } catch (_) {}
  };

  const phone = user?.phone || user?.mobile || "";
  const maskedPhone = phone ? `+91 ${String(phone).replace(/^91/, "").slice(0, 5)}*****` : "—";
  const initial = (user?.name || phone || "U").charAt(0).toUpperCase();

  const menuItems = [
    { icon: <User size={20} />,     iconBg: "#7c3aed", label: "My Account",   sub: "Bets, casino, D&W, bonus",   path: "/account" },
    { icon: <Wallet size={20} />,   iconBg: "#4f46e5", label: "Wallet",        sub: "Deposits & withdrawals",       path: "/wallet" },
    { icon: <Building2 size={20} />,iconBg: "#0f172a", label: "Bank Account",  sub: "Manage bank details",          path: "/bank-accounts" },
    { icon: <Gift size={20} />,     iconBg: "#ea580c", label: "Refer & Earn",  sub: "Invite friends, earn bonus",   path: "/refer" },
    { icon: <Share2 size={20} />,   iconBg: "#2563eb", label: "Share App",     sub: "Send referral link",           action: shareApp },
  ];

  return (
    <div style={page}>
      {/* Header card */}
      <div style={headerCard}>
        <div style={avatarCircle}>{initial}</div>
        <div style={userInfo}>
          <div style={userName}>{user?.name || "Player"}</div>
          <div style={userPhone}>{maskedPhone}</div>
          <div style={balBadge}>Balance: <strong>₹{Number(wallet.balance || 0).toFixed(2)}</strong></div>
        </div>
      </div>

      {/* Menu */}
      <div style={menuCard}>
        {menuItems.map((item) => (
          <button
            key={item.label}
            type="button"
            style={menuRow}
            onClick={item.action || (() => navigate(item.path))}
          >
            <span style={{ ...menuIcon, background: item.iconBg }}>{item.icon}</span>
            <span style={menuText}>
              <span style={menuLabel}>{item.label}</span>
              <span style={menuSub}>{item.sub}</span>
            </span>
            <ChevronRight size={18} color="#cbd5e1" />
          </button>
        ))}
      </div>

      {/* Logout */}
      <button type="button" style={logoutBtn} onClick={logout}>
        <LogOut size={16} /> Logout
      </button>

      <BottomNav />
    </div>
  );
}

const page = { minHeight: "100vh", background: "#f3f4f7", paddingBottom: 88, fontFamily: "'Segoe UI', Inter, system-ui, sans-serif" };

const headerCard = {
  background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
  padding: "32px 20px 24px",
  display: "flex",
  alignItems: "center",
  gap: 16,
};
const avatarCircle = {
  width: 68,
  height: 68,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.25)",
  border: "3px solid rgba(255,255,255,0.6)",
  display: "grid",
  placeItems: "center",
  fontSize: 28,
  fontWeight: 900,
  color: "#fff",
  flexShrink: 0,
};
const userInfo = { display: "flex", flexDirection: "column", gap: 4 };
const userName = { fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px" };
const userPhone = { fontSize: 14, color: "rgba(255,255,255,0.85)", fontWeight: 600 };
const balBadge = { marginTop: 4, display: "inline-flex", gap: 4, alignItems: "center", background: "rgba(255,255,255,0.2)", borderRadius: 999, padding: "4px 12px", fontSize: 13, color: "#fff", fontWeight: 700 };

const menuCard = { margin: "16px 14px 0", background: "#fff", borderRadius: 18, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.06)" };
const menuRow = { width: "100%", border: 0, borderBottom: "1px solid #f1f5f9", background: "#fff", display: "flex", alignItems: "center", gap: 14, padding: "15px 16px", cursor: "pointer" };
const menuIcon = { width: 42, height: 42, borderRadius: 13, display: "grid", placeItems: "center", color: "#fff", flexShrink: 0 };
const menuText = { flex: 1, display: "flex", flexDirection: "column", gap: 2, textAlign: "left" };
const menuLabel = { fontSize: 15, fontWeight: 700, color: "#1e293b" };
const menuSub = { fontSize: 11, color: "#94a3b8" };

const logoutBtn = {
  margin: "14px 14px 0",
  width: "calc(100% - 28px)",
  height: 48,
  border: "1.5px solid #fca5a5",
  borderRadius: 14,
  background: "#fff",
  color: "#dc2626",
  fontWeight: 800,
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: "pointer",
};

export default Profile;
